import React, { useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  LinearProgress,
  Paper,
  Typography,
  useTheme,
  alpha,
  Collapse,
  Tooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningIcon from "@mui/icons-material/Warning";
import ErrorIcon from "@mui/icons-material/Error";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import axios from "axios";
import { hostname } from "../utils";
import LoadingButton from "./LoadingButton";

// Spam score metric categories
interface SpamMetric {
  name: string;
  score: number; // 0-10
  description: string;
  issues: string[];
  suggestions: string[];
}

interface SpamAnalysisResult {
  overallScore: number; // 0-100
  verdict: "excellent" | "good" | "average" | "poor" | "spam";
  metrics: SpamMetric[];
  spamWordsFound: string[];
  improvedSubject?: string;
  improvedContent?: string;
  summary: string;
}

interface SpamCheckerDialogProps {
  open: boolean;
  onClose: () => void;
  subject: string;
  content: string;
  getToken: () => Promise<string>;
  companyId: string;
  onApplyRecommendations?: (data: { subject?: string; content?: string }) => void;
}

// Score zone colors and labels
const getScoreZone = (score: number): { color: string; label: string; bgColor: string } => {
  if (score >= 80) return { color: "#4caf50", label: "Excellent", bgColor: "rgba(76, 175, 80, 0.1)" };
  if (score >= 60) return { color: "#8bc34a", label: "Good", bgColor: "rgba(139, 195, 74, 0.1)" };
  if (score >= 40) return { color: "#ff9800", label: "Average", bgColor: "rgba(255, 152, 0, 0.1)" };
  if (score >= 20) return { color: "#f44336", label: "Poor", bgColor: "rgba(244, 67, 54, 0.1)" };
  return { color: "#d32f2f", label: "Spam Risk", bgColor: "rgba(211, 47, 47, 0.1)" };
};

// Get metric score color
const getMetricColor = (score: number): string => {
  if (score >= 8) return "#4caf50";
  if (score >= 6) return "#8bc34a";
  if (score >= 4) return "#ff9800";
  if (score >= 2) return "#f44336";
  return "#d32f2f";
};

// Metric score bar component
const MetricScoreBar: React.FC<{ metric: SpamMetric; expanded: boolean; onToggle: () => void }> = ({
  metric,
  expanded,
  onToggle,
}) => {
  const theme = useTheme();
  const color = getMetricColor(metric.score);
  const percentage = (metric.score / 10) * 100;

  return (
    <Paper
      variant="outlined"
      sx={{
        mb: 1.5,
        overflow: "hidden",
        borderColor: "divider",
        backgroundColor: "background.paper",
      }}
    >
      <Box
        onClick={onToggle}
        sx={{
          p: 1.5,
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          gap: 2,
          "&:hover": {
            backgroundColor: "action.hover",
          },
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.5 }}>
            <Typography variant="body2" fontWeight={500}>
              {metric.name}
            </Typography>
            <Typography
              variant="body2"
              fontWeight={600}
              sx={{ color }}
            >
              {metric.score}/10
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={percentage}
            sx={{
              height: 6,
              borderRadius: 3,
              backgroundColor: alpha(color, 0.15),
              "& .MuiLinearProgress-bar": {
                backgroundColor: color,
                borderRadius: 3,
              },
            }}
          />
        </Box>
        <IconButton size="small" sx={{ color: "text.secondary" }}>
          {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
        </IconButton>
      </Box>

      <Collapse in={expanded}>
        <Box sx={{ px: 1.5, pb: 1.5, pt: 0 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
            {metric.description}
          </Typography>

          {metric.issues.length > 0 && (
            <Box sx={{ mb: 1 }}>
              <Typography variant="caption" fontWeight={600} color="error.main" sx={{ display: "block", mb: 0.5 }}>
                Issues Found:
              </Typography>
              {metric.issues.map((issue, idx) => (
                <Box key={idx} sx={{ display: "flex", alignItems: "flex-start", gap: 0.5, ml: 1 }}>
                  <ErrorIcon sx={{ fontSize: 12, color: "error.main", mt: 0.3 }} />
                  <Typography variant="caption" color="text.secondary">
                    {issue}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}

          {metric.suggestions.length > 0 && (
            <Box>
              <Typography variant="caption" fontWeight={600} color="primary.main" sx={{ display: "block", mb: 0.5 }}>
                Suggestions:
              </Typography>
              {metric.suggestions.map((suggestion, idx) => (
                <Box key={idx} sx={{ display: "flex", alignItems: "flex-start", gap: 0.5, ml: 1 }}>
                  <CheckCircleIcon sx={{ fontSize: 12, color: "primary.main", mt: 0.3 }} />
                  <Typography variant="caption" color="text.secondary">
                    {suggestion}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
        </Box>
      </Collapse>
    </Paper>
  );
};

// Pentagon/Radar chart component for visual representation
const RadarChart: React.FC<{ metrics: SpamMetric[] }> = ({ metrics }) => {
  const theme = useTheme();
  const size = 200;
  const center = size / 2;
  const radius = 70;
  const levels = 5;

  // Calculate points for each metric
  const angleStep = (2 * Math.PI) / metrics.length;
  const startAngle = -Math.PI / 2; // Start from top

  const getPoint = (index: number, value: number) => {
    const angle = startAngle + index * angleStep;
    const r = (value / 10) * radius;
    return {
      x: center + r * Math.cos(angle),
      y: center + r * Math.sin(angle),
    };
  };

  // Create polygon path
  const polygonPoints = metrics
    .map((metric, i) => {
      const point = getPoint(i, metric.score);
      return `${point.x},${point.y}`;
    })
    .join(" ");

  // Create level circles
  const levelCircles = Array.from({ length: levels }, (_, i) => {
    const levelRadius = ((i + 1) / levels) * radius;
    const points = metrics
      .map((_, j) => {
        const angle = startAngle + j * angleStep;
        return `${center + levelRadius * Math.cos(angle)},${center + levelRadius * Math.sin(angle)}`;
      })
      .join(" ");
    return points;
  });

  return (
    <Box sx={{ display: "flex", justifyContent: "center", my: 2 }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        {/* Background levels */}
        {levelCircles.map((points, i) => (
          <polygon
            key={i}
            points={points}
            fill="none"
            stroke={theme.palette.divider}
            strokeWidth="1"
            opacity={0.5}
          />
        ))}

        {/* Axis lines */}
        {metrics.map((_, i) => {
          const point = getPoint(i, 10);
          return (
            <line
              key={i}
              x1={center}
              y1={center}
              x2={point.x}
              y2={point.y}
              stroke={theme.palette.divider}
              strokeWidth="1"
              opacity={0.5}
            />
          );
        })}

        {/* Data polygon */}
        <polygon
          points={polygonPoints}
          fill={alpha(theme.palette.primary.main, 0.2)}
          stroke={theme.palette.primary.main}
          strokeWidth="2"
        />

        {/* Data points */}
        {metrics.map((metric, i) => {
          const point = getPoint(i, metric.score);
          const color = getMetricColor(metric.score);
          return (
            <circle key={i} cx={point.x} cy={point.y} r={5} fill={color} stroke="#fff" strokeWidth="2" />
          );
        })}

        {/* Labels */}
        {metrics.map((metric, i) => {
          const labelPoint = getPoint(i, 12);
          const anchor = labelPoint.x < center - 10 ? "end" : labelPoint.x > center + 10 ? "start" : "middle";
          return (
            <text
              key={i}
              x={labelPoint.x}
              y={labelPoint.y}
              textAnchor={anchor}
              dominantBaseline="middle"
              fill={theme.palette.text.secondary}
              fontSize="10"
              fontFamily={theme.typography.fontFamily}
            >
              {metric.name.split(" ")[0]}
            </text>
          );
        })}
      </svg>
    </Box>
  );
};

const SpamCheckerDialog: React.FC<SpamCheckerDialogProps> = ({
  open,
  onClose,
  subject,
  content,
  getToken,
  companyId,
  onApplyRecommendations,
}) => {
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [applyingRecommendations, setApplyingRecommendations] = useState(false);
  const [result, setResult] = useState<SpamAnalysisResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [expandedMetrics, setExpandedMetrics] = useState<Set<string>>(new Set());

  const analyzeSpam = async () => {
    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const token = await getToken();
      const response = await axios.post(
        `${hostname}/content/spam-check`,
        {
          companyId,
          subject,
          content,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setResult(response.data);

      // Auto-expand metrics with low scores
      const lowScoreMetrics = response.data.metrics
        .filter((m: SpamMetric) => m.score < 6)
        .map((m: SpamMetric) => m.name);
      setExpandedMetrics(new Set(lowScoreMetrics));
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to analyze email for spam");
    } finally {
      setLoading(false);
    }
  };

  const handleApplyRecommendations = () => {
    if (result && onApplyRecommendations) {
      setApplyingRecommendations(true);
      onApplyRecommendations({
        subject: result.improvedSubject,
        content: result.improvedContent,
      });
      setTimeout(() => {
        setApplyingRecommendations(false);
        onClose();
      }, 500);
    }
  };

  const toggleMetric = (name: string) => {
    setExpandedMetrics((prev) => {
      const next = new Set(prev);
      if (next.has(name)) {
        next.delete(name);
      } else {
        next.add(name);
      }
      return next;
    });
  };

  const scoreZone = result ? getScoreZone(result.overallScore) : null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Typography variant="h6">Spam Analysis</Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        {!result && !loading && !error && (
          <Box sx={{ textAlign: "center", py: 3 }}>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
              Analyze your email content to check for spam triggers, deliverability issues, and get
              recommendations for improvement.
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              The analysis will check for spam words, formatting issues, urgency triggers, and more.
            </Typography>
            <LoadingButton variant="contained" onClick={analyzeSpam} loading={loading}>
              Analyze Email
            </LoadingButton>
          </Box>
        )}

        {loading && (
          <Box sx={{ textAlign: "center", py: 4 }}>
            <CircularProgress size={40} />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Analyzing your email for spam indicators...
            </Typography>
          </Box>
        )}

        {error && (
          <Box sx={{ textAlign: "center", py: 3 }}>
            <ErrorIcon color="error" sx={{ fontSize: 48, mb: 1 }} />
            <Typography variant="body1" color="error" sx={{ mb: 2 }}>
              {error}
            </Typography>
            <Button variant="outlined" onClick={analyzeSpam}>
              Try Again
            </Button>
          </Box>
        )}

        {result && (
          <Box>
            {/* Overall Score Section */}
            <Paper
              sx={{
                p: 2,
                mb: 3,
                backgroundColor: scoreZone?.bgColor,
                border: 1,
                borderColor: alpha(scoreZone?.color || "#000", 0.3),
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Box sx={{ position: "relative", display: "inline-flex" }}>
                  <CircularProgress
                    variant="determinate"
                    value={result.overallScore}
                    size={80}
                    thickness={4}
                    sx={{
                      color: scoreZone?.color,
                      backgroundColor: alpha(scoreZone?.color || "#000", 0.1),
                      borderRadius: "50%",
                    }}
                  />
                  <Box
                    sx={{
                      top: 0,
                      left: 0,
                      bottom: 0,
                      right: 0,
                      position: "absolute",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Typography variant="h5" fontWeight={700} sx={{ color: scoreZone?.color }}>
                      {result.overallScore}
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h6" fontWeight={600} sx={{ color: scoreZone?.color }}>
                    {scoreZone?.label}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {result.summary}
                  </Typography>
                </Box>
              </Box>
            </Paper>

            {/* Radar Chart */}
            {result.metrics.length >= 3 && <RadarChart metrics={result.metrics} />}

            <Divider sx={{ my: 2 }} />

            {/* Detailed Metrics */}
            <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
              Detailed Analysis
            </Typography>
            {result.metrics.map((metric) => (
              <MetricScoreBar
                key={metric.name}
                metric={metric}
                expanded={expandedMetrics.has(metric.name)}
                onToggle={() => toggleMetric(metric.name)}
              />
            ))}

            {/* Spam Words Found */}
            {result.spamWordsFound.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" color="error.main" sx={{ mb: 1, display: "flex", alignItems: "center", gap: 0.5 }}>
                  <WarningIcon fontSize="small" />
                  Spam Words Detected ({result.spamWordsFound.length})
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                  {result.spamWordsFound.map((word, idx) => (
                    <Box
                      key={idx}
                      sx={{
                        px: 1,
                        py: 0.25,
                        borderRadius: 1,
                        backgroundColor: alpha(theme.palette.error.main, 0.1),
                        border: 1,
                        borderColor: alpha(theme.palette.error.main, 0.3),
                      }}
                    >
                      <Typography variant="caption" color="error.main">
                        {word}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}

            {/* Apply Recommendations */}
            {(result.improvedSubject || result.improvedContent) && (
              <Box sx={{ mt: 3 }}>
                <Divider sx={{ mb: 2 }} />
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <Box>
                    <Typography variant="subtitle2" sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      <AutoFixHighIcon fontSize="small" color="primary" />
                      Improved Version Available
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Apply AI-optimized subject and content to reduce spam risk
                    </Typography>
                  </Box>
                  <Tooltip title="Replace your current email with the improved version">
                    <LoadingButton
                      variant="contained"
                      size="small"
                      onClick={handleApplyRecommendations}
                      loading={applyingRecommendations}
                      startIcon={<AutoFixHighIcon />}
                    >
                      Apply
                    </LoadingButton>
                  </Tooltip>
                </Box>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="inherit">
          Close
        </Button>
        {result && (
          <Button variant="outlined" onClick={analyzeSpam}>
            Re-analyze
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default SpamCheckerDialog;
